import { Message } from "element-ui";
import swal from "sweetalert2";
import Vue from "vue";
import omitDeep from "../helpers/omitDeep";
import apollo from "./apollo-graphql";

const vm = new Vue();

function getGraphQLErrors(error) {
    if (!error) return "Unknown error.";
    if (error.graphQLErrors && error.graphQLErrors.length) {
        return error.graphQLErrors.map((err) => err.message);
    }

    if (
        error.networkError &&
        error.networkError.result &&
        error.networkError.result.errors
    ) {
        return error.networkError.result.errors.map((err) => err.message);
    }

    const message = error && (error.message || error);
    if (message && typeof message === "string") {
        return [message.replace("GraphQL error:", "")];
    }
    return [message];
}

function getErrorsFormattedAsHTML(error) {
    const errors = getGraphQLErrors(error);
    return errors.join("<br/>");
}

function getErrorsFormattedAsVNode(error, title) {
    const errors = getGraphQLErrors(error);
    const h = vm.$createElement;
    const children = errors.map((err) =>
        h("p", { style: "color: #f56c6c" }, err),
    );
    if (title) {
        children.unshift(h("strong", { style: "color: #424c67" }, title));
    }
    return h("div", null, children);
}

function errorAlert(error) {
    const message = getErrorsFormattedAsHTML(error);
    swal({
        title: "Error!",
        html: message,
        buttonsStyling: false,
        confirmButtonClass: "btn btn-danger btn-fill",
        type: "error",
    });
}

export const fetch = {
    /**
     * Wraps the given query in a UI loader if loading takes
     * too long. Swallows errors and displays them in a popup
     * @param {Object} options The apollo graphql request
     */
    async query(request) {
        return new Promise(async (resolve) => {
            try {
                const result = await vm.$loader(() => apollo.query(request));
                resolve(result);
            } catch (error) {
                console.error(error);
                errorAlert(error);
            }
        });
    },
    /**
     * Wraps the given mutation in a UI loader if loading takes
     * too long. Shows toast message if successful
     * Swallows errors and displays them in a popup.
     * @param {Object} request The apollo graphql options
     * @param {Object} options The options for the UI loader/toaster
     * options: { showToast: Boolean, showErrorAsToast: Boolean,
     *            message: String, title: String }
     */
    async mutate(request, options) {
        const defaults = {
            showToast: true,
            showErrorAsToast: false,
            message: "Updated",
            title: "",
        };
        const settings = { ...defaults, ...options };
        return new Promise(async (resolve) => {
            try {
                // remove the __typename vairable graphQL sticks in...
                // and remove other common variables we don't want to send/update
                const variables = omitDeep(request.variables, [
                    "__typename",
                    "createdAt",
                    "updatedAt",
                    "createdBy",
                    "updatedBy",
                ]);
                const result = await vm.$loader(
                    () =>
                        apollo.mutate({
                            mutation: request.mutation,
                            variables,
                        }),
                    options,
                );
                if (settings.showToast) {
                    Message({
                        message: settings.message,
                        type: "success",
                    });
                    resolve(result);
                } else {
                    resolve(result);
                }
            } catch (error) {
                if (settings.showErrorAsToast) {
                    const message = getErrorsFormattedAsVNode(
                        error,
                        settings.title,
                    );
                    Message({
                        message,
                        type: "error",
                    });
                } else {
                    console.error(error);
                    errorAlert(error);
                }
            }
        });
    },
};
